import { Fragment } from 'react';
import { useLocation } from 'react-router';
import { Button, Typography } from 'antd';
import { motion, AnimatePresence } from 'motion/react';
import { useBoolean, useReleaseIdentifier } from '@admin-panel/hooks';
import CoyuLogo from '../CoyuLogo';
import { NAVIGATION_APPLICATIONS } from './constants';
import * as Styles from './styles';
import { useLogoutMutation } from '@admin-panel/pages/synq/store/identity/identityApi';

const { Text } = Typography;

const Navigation = () => {
  const [isExpanded, expandedActions] = useBoolean();
  const [logout, { isLoading }] = useLogoutMutation();

  const location = useLocation();
  const { release } = useReleaseIdentifier();

  return (
    <>
      <Styles.Wrapper
        animate={isExpanded ? 'expanded' : 'collapsed'}
        variants={{
          collapsed: { width: 70 },
          expanded: { width: 260 },
        }}
        transition={{
          duration: 0.2,
        }}
        onMouseEnter={expandedActions.on}
        onMouseLeave={expandedActions.off}
      >
        <Styles.Header
          variants={{
            collapsed: { padding: '0 20px' },
            expanded: { padding: '0 24px' },
          }}
        >
          <CoyuLogo mode={isExpanded ? 'LONG' : 'COMPACT'} fill="#ffffff" />
        </Styles.Header>

        <Styles.NavList
          collapsed={!isExpanded}
          initial={false}
          animate={isExpanded ? 'expanded' : 'collapsed'}
          variants={{
            expanded: { opacity: 1, transition: { staggerChildren: 0.04 } },
            collapsed: { opacity: 1, transition: { staggerChildren: 0.02 } },
          }}
        >
          {NAVIGATION_APPLICATIONS.map(app => (
            <Fragment key={app.application}>
              <Styles.NavItem
                collapsed={!isExpanded}
                to={app.features[0]?.link}
                initial={false}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background:
                    location.pathname.startsWith(app.features[0]?.link) ||
                    app.features.some(f => location.pathname.startsWith(f.link))
                      ? '#333'
                      : undefined,
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <span
                    style={{
                      width: 24,
                      minWidth: 24,
                      maxWidth: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'none',
                    }}
                  >
                    {app.icon}
                  </span>
                  <motion.span
                    key="app-label"
                    animate={{
                      opacity: isExpanded ? 1 : 0,
                      width: isExpanded ? 90 : 0,
                      marginLeft: isExpanded ? 12 : 0,
                    }}
                    transition={{ duration: 0.18 }}
                    style={{
                      color: 'white',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      display: 'inline-block',
                      width: isExpanded ? 90 : 0,
                      minWidth: 0,
                      pointerEvents: isExpanded ? 'auto' : 'none',
                      verticalAlign: 'middle',
                    }}
                  >
                    {app.application}
                  </motion.span>
                </span>
              </Styles.NavItem>

              <AnimatePresence>
                {isExpanded && (
                  <Styles.NestedNavList
                    key={app.application + '-nested'}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {app.features.map(feature => (
                      <Styles.NavItem
                        collapsed={!isExpanded}
                        key={feature.title}
                        to={feature.link}
                        initial={false}
                        animate={isExpanded ? 'expanded' : 'collapsed'}
                        variants={{
                          expanded: { opacity: 1, x: 0 },
                          collapsed: { opacity: 0, x: -20 },
                        }}
                        transition={{
                          type: 'spring',
                          stiffness: 300,
                          damping: 30,
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          background: location.pathname.startsWith(feature.link)
                            ? '#222'
                            : undefined,
                        }}
                      >
                        {feature.icon}
                        <Text style={{ color: 'white' }}>{feature.title}</Text>
                      </Styles.NavItem>
                    ))}
                  </Styles.NestedNavList>
                )}
              </AnimatePresence>
            </Fragment>
          ))}
        </Styles.NavList>

        <Styles.Footer
          variants={{
            collapsed: { opacity: 0, display: 'none' },
            expanded: { opacity: 1 },
          }}
        >
          <Button
            loading={isLoading}
            onClick={() => {
              void logout();
            }}
          >
            Logout
          </Button>

          {release && <Text style={{ color: 'white', fontSize: 10 }}>{release}</Text>}
        </Styles.Footer>
      </Styles.Wrapper>
      <AnimatePresence>
        {isExpanded && (
          <Styles.Overlay
            key="overlay"
            initial={{ opacity: 0, left: 70, width: 'calc(100vw - 70px)' }}
            animate={{
              opacity: 1,
              left: 260,
              width: 'calc(100vw - 260px)',
              backdropFilter: 'blur(2px)',
              // @ts-expect-error shut up TS
              WebkitBackdropFilter: 'blur(2px)',
            }}
            // exit={{ opacity: 0 }}
            transition={{
              duration: 0.3,
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              if (e.target === e.currentTarget) {
                expandedActions.off();
              }
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
